import { useState, useEffect, useMemo } from "react";
import {
  DashboardSystemCurrentTimeResponse,
  useDashboardSystemCurrentTimeQuery,
} from "@/shared/api/lod-api";
import { formatTimeDifference } from "../lib/helpers";
import { selectDateRange, selectSelectedOrder } from "@/entities/annotation";
import { useAppSelector } from "@/app/appStore";

interface UseSystemTimeReturn {
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
  currentTime: Date;
  lastFetchedTime: Date | null;
  formattedTimeElapsed: string;
  retrySeconds: number;
  data?: DashboardSystemCurrentTimeResponse["result"];
}

export const useSystemTime = (): UseSystemTimeReturn => {
  const dateRange = useAppSelector(selectDateRange);
  const selectedOrder = useAppSelector(selectSelectedOrder);

  const [currentTime, setCurrentTime] = useState(new Date());
  const [lastFetchedTime, setLastFetchedTime] = useState<Date | null>(null);
  const [isPollingDisabled, setIsPollingDisabled] = useState(false);

  const pollingInterval = useMemo(() => {
    if (isPollingDisabled || !dateRange.end) {
      return undefined;
    }
    return 30_000;
  }, [isPollingDisabled, dateRange.end]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const {
    data: systemTimeData,
    refetch,
    isFetching,
    isLoading,
    isError,
    error,
  } = useDashboardSystemCurrentTimeQuery(undefined, {
    pollingInterval,
    refetchOnMountOrArgChange: true,
    skip: !selectedOrder,
  });

  useEffect(() => {
    if (!systemTimeData?.result?.system_datetime || !dateRange.end) {
      return;
    }

    const systemTime = new Date(systemTimeData.result?.system_datetime);
    if (systemTime > dateRange.end && !isPollingDisabled) {
      setIsPollingDisabled(true);
    }
  }, [
    systemTimeData?.result.system_datetime,
    dateRange.end,
    isPollingDisabled,
  ]);

  // Refetch system time when date range changes
  useEffect(() => {
    if (selectedOrder) {
      refetch();
      setIsPollingDisabled(false);
    }
  }, [dateRange.start, dateRange.end, selectedOrder]);

  useEffect(() => {
    if (systemTimeData && !isFetching) {
      setLastFetchedTime(new Date());
    }
  }, [systemTimeData, isFetching]);

  const formattedTimeElapsed = useMemo(() => {
    if (isLoading || !lastFetchedTime) {
      return "-";
    }
    return formatTimeDifference(
      currentTime.getTime() - lastFetchedTime.getTime()
    );
  }, [isLoading, lastFetchedTime, currentTime]);

  const retrySeconds = useMemo(() => {
    if (!lastFetchedTime) return 30;
    const elapsedSeconds = Math.floor(
      (currentTime.getTime() - lastFetchedTime.getTime()) / 1000
    );
    const remainingSeconds = Math.max(0, 30 - elapsedSeconds);
    return remainingSeconds;
  }, [lastFetchedTime, currentTime]);

  return {
    data: systemTimeData?.result,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
    currentTime,
    lastFetchedTime,
    formattedTimeElapsed,
    retrySeconds,
  };
};
